import React, { useContext, useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import CountryRegionSelectorComponent from "../../../../../components/CountrySelect/CountrySelect";
import UploadFile from "../../../../../components/uploadFile/UploadFile";
import { useNavigate } from "react-router-dom";
import { IoIosAddCircleOutline } from "react-icons/io";
import { FaTimes } from "react-icons/fa";
import AlertContext from "../../../../../Context/Alert/AlertContext";
import { BaseURL } from "../../../../../Files/BaseURL";
import { formatDateForInput } from "../../../../../Files/UseFunction";

const FreelancerProfile = () => {
  const navigate = useNavigate();
  const { showAlert } = useContext(AlertContext);
  const [CountryList, setCountryList] = useState([]);
  const [Files, setFiles] = useState([]);
  const [PersonalInfo, setPersonalInfo] = useState(null);
  const [Description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation states
  const [errors, setErrors] = useState({
    description: "",
    files: ""
  });
  const [isFormValid, setIsFormValid] = useState(false);

  // Validate form whenever description or files change
  useEffect(() => {
    validateForm();
  }, [Description, Files]);

  const validateForm = () => {
    const newErrors = {
      description: "",
      files: ""
    };

    // Description validation
    if (!Description.trim()) {
      newErrors.description = "Description is required";
    } else if (Description.trim().length < 50) {
      newErrors.description = "Description must be at least 50 characters long";
    }

    // File validation
    if (Files.length === 0) {
      newErrors.files = "At least one file is required";
    }

    setErrors(newErrors);
    setIsFormValid(!newErrors.description && !newErrors.files);
  };

  // Fetch Countries
  const fetchCountry = async () => {
    try {
      const response = await fetch(`${BaseURL}/api/auth/getcountry`);
      if (!response.ok) {
        showAlert("Failed to fetch countries", "danger");
      }
      const data = await response.json();
      setCountryList(data.Country);
    } catch (error) {
      showAlert(error.message, "danger");
    }
  };

  useEffect(() => {
    fetchCountry();
  }, []);

  // Fetch User Information
  const fetchuser = async () => {
    try {
      const authToken = localStorage.getItem("p@token");
      const response = await fetch(
        `${BaseURL}/api/business/freelancesetup/userinfo`,
        {
          headers: {
            "auth-token": authToken,
          },
        }
      );
      if (!response.ok) {
        showAlert("Unable to fetch user", "danger");
        return;
      }
      const data = await response.json();
      setPersonalInfo({
        ...data,
        DOB: formatDateForInput(data?.DOB),
      });
    } catch (e) {
      showAlert("Unable to fetch user", "danger");
    }
  };

  useEffect(() => {
    fetchuser();
  }, []);

  // Handle File Selection with validation
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file => {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showAlert(`${file.name} is too large. Maximum size is 5MB`, "danger");
        return false;
      }
      // Check file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        showAlert(`${file.name} is not a supported file type`, "danger");
        return false;
      }
      return true;
    });

    setFiles(prevFiles => [...prevFiles, ...validFiles]);
  };

  // Handle File Removal
  const removeFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  // Submit Handler with validation
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) {
      showAlert("Please fix the validation errors before submitting", "danger");
      return;
    }

    setIsSubmitting(true);

    try {
      const authToken = localStorage.getItem("p@token");
      if (!authToken) {
        showAlert("Authentication token not found. Please log in.", "danger");
        setIsSubmitting(false);
        return;
      }

      const formData = new FormData();
      formData.append("Description", Description);

      Files.forEach((file) => {
        formData.append("files", file);
      });

      const response = await fetch(
        `${BaseURL}/api/business/freelancesetup/freelancesetup`,
        {
          method: "PUT",
          headers: {
            "auth-token": authToken,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (response.ok) {
        showAlert(result.message || "Profile created successfully!", "success");
        navigate("/profileCreation/freelancer/identityDon");
      } else {
        showAlert(
          result.message || "An error occurred while creating the profile.",
          "danger"
        );
      }
    } catch (error) {
      console.error("Error submitting the form:", error);
      showAlert(
        "An unexpected error occurred. Please try again later.",
        "danger"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="h-2 w-full bg-neutral-200 dark:bg-neutral-600">
        <div className="h-2 bg-green-500" style={{ width: "50%" }}></div>
      </div>
      <div className="py-2 flex justify-center items-center w-[90%] md:w-[60%] xl:w-[30%] m-auto">
        <div className="card bg-white font-pop p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-4 text-center">
            Create Profile
          </h2>
          <p className="text-gray-600 mb-6">Personal Information</p>
          <form className="flex flex-col gap-2" onSubmit={handleSubmit}>
            {/* First Name (Disabled) */}
            <input
              type="text"
              id="fname"
              value={PersonalInfo?.FirstName || ""}
              disabled={true}
              placeholder="First Name"
              className="w-full px-6 border-2 border-transparent font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
            />

            {/* Last Name */}
            <input
              type="text"
              id="lname"
              value={PersonalInfo?.LastName || ""}
              placeholder="Last Name"
              className="w-full px-6 border-2 border-transparent font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
            />

            {/* Date of Birth */}
            <input
              type="date"
              min="1900-01-01"
              value={PersonalInfo?.DOB || ""}
              className="w-full px-6 border-2 border-transparent font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
            />

            <hr className="mb-3" />
            <p className="text-gray-600 mb-6">Address</p>

            {/* Street Address */}
            <input
              type="text"
              id="streetA"
              value={PersonalInfo?.Homeaddress?.Address || ""}
              disabled
              placeholder="Street Address"
              className="w-full px-6 border-2 border-transparent font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
            />

            {/* City */}
            <input
              type="text"
              id="city"
              value={PersonalInfo?.Homeaddress?.City || ""}
              disabled
              placeholder="City"
              className="w-full px-6 border-2 border-transparent font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
            />

            {/* Country */}
            <input
              type="text"
              id="country"
              value={PersonalInfo?.Homeaddress?.Country || ""}
              disabled
              placeholder="Country"
              className="w-full px-6 border-2 border-transparent font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
            />

            {/* State and Zip Code */}
            <div className="mb-4 flex justify-between gap-2">
              <input
                name="state"
                id="state"
                value={PersonalInfo?.Homeaddress?.State || ""}
                disabled
                placeholder="State"
                className="w-full px-6 border-2 border-transparent font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
              />
              <input
                type="number"
                id="zip-code"
                name="zip-code"
                placeholder="Zip Code"
                value={PersonalInfo?.Homeaddress?.Zip || ""}
                disabled
                className="w-full px-6 border-2 border-transparent font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
              />
            </div>

            <hr className="mb-3" />
            <p className="text-gray-600 mb-6">
              Describe the purpose of your business
            </p>

            {/* Description with validation */}
            <div className="relative">
              <textarea
                name="description"
                id="description"
                cols="30"
                value={Description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                rows="5"
                placeholder="Description"
                className={`w-full px-6 border-2 ${errors.description ? "border-red-500" : "border-transparent"
                  } font-pop text-lg rounded-2xl bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none`}
              ></textarea>
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">{errors.description}</p>
              )}
            </div>

            {/* File Upload with validation */}
            <div className="flex items-center justify-center w-full">
              <label
                htmlFor="dropzone-file"
                className={`flex flex-col items-center justify-center w-full border-2 ${errors.files ? "border-red-500" : "border-primarygreen"
                  } border-dashed rounded-lg cursor-pointer bg-transparent dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600`}
              >
                <div className="flex flex-row gap-4 items-center justify-center py-3 text-primarygreen font-pop">
                  <IoIosAddCircleOutline className="text-2xl" />
                  <p className="text-lg">Upload file</p>
                </div>
                <input
                  id="dropzone-file"
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
              </label>
            </div>
            {errors.files && (
              <p className="text-red-500 text-sm mt-1">{errors.files}</p>
            )}

            {/* Display Selected Files */}
            {Files.length > 0 && (
              <div className="mt-4">
                <ul className="space-y-2 flex gap-2 flex-wrap">
                  {Files.map((file, index) => (
                    <li
                      key={index}
                      className="flex items-center gap-1 justify-between bg-gray-100 p-2 rounded"
                    >
                      <span className="text-gray-700">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-700"
                        aria-label={`Remove ${file.name}`}
                      >
                        <FaTimes />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Next Button */}
            <button
              type="submit"
              disabled={isSubmitting || !isFormValid}
              className={`my-4 bg-green-500 hover:bg-transparent hover:bg-white-500 font-semibold hover:text-black py-2 px-4 w-full border border-black-500 hover:border-green-500 rounded-full text-white ${(isSubmitting || !isFormValid) ? "opacity-50 cursor-not-allowed" : ""
                } animate__animated animate__backInUp`}
            >
              {isSubmitting ? "Submitting..." : "Next"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default FreelancerProfile;
